import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Flex, Button, Text, Select } from '@radix-ui/themes';
import { format } from 'date-fns';

import RadixDialog from '../../../shared/RadixDialog';
import DatePicker from '../../../shared/DatePicker/DatePicker.jsx';
import styles from './ScheduleModal.module.css';
import {
  timeOptions15Min as generateAllTimeOptions,
  currentDayTimeOptions15Min as currentDayTimeOptions,
  isTodayInZone,
  timeToMinutes,
} from '../../../containers/campaigns/utils/helpers';

const DEFAULT_TIME = timeToMinutes(9, 0); // 9:00 AM in minutes from midnight

export const ScheduleModal = ({
  open,
  onClose,
  onSchedule,
  isEdit = false,
}) => {
  const { t } = useTranslation('broadcasts');
  const { timezone, timezoneAbbr } = useSelector(state => state.currentBoard);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(DEFAULT_TIME); // Minutes from midnight
  const [error, setError] = useState('');

  const isCurrentDay = selectedDate && isTodayInZone(timezone, selectedDate);

  // Get time options based on whether today is selected
  const timeOptions = isCurrentDay
    ? currentDayTimeOptions(timezoneAbbr, timezone)
    : generateAllTimeOptions(timezoneAbbr);

  const firstAvailableTime = timeOptions[0]?.value;

  // Auto-select first available time when date changes to today
  useEffect(() => {
    if (isCurrentDay && firstAvailableTime !== undefined) {
      setSelectedTime(firstAvailableTime);
    }
  }, [isCurrentDay, firstAvailableTime]);

  const handleSchedule = () => {
    if (!selectedDate) {
      setError(t('scheduling.dateRequired'));
      return;
    }

    if (selectedTime === null || selectedTime === undefined) {
      setError(t('scheduling.timeRequired'));
      return;
    }

    setError('');

    // Pass date and time (minutes from midnight) - backend handles timezone conversion
    // using the board's timezone (same pattern as ChangeDate.jsx)
    onSchedule({
      scheduledDate: format(selectedDate, 'yyyy-MM-dd'),
      scheduledHour: selectedTime, // minutes from midnight, stored in scheduled_hour column
    });

    handleClose();
  };

  const handleClose = () => {
    setSelectedDate(null);
    setSelectedTime(DEFAULT_TIME);
    setError('');
    onClose();
  };

  const handleDateChange = dates => {
    if (dates && dates.length > 0) {
      const newDate = dates[0];
      setSelectedDate(newDate);
      setError('');

      // Reset time when date changes - will be auto-set by useEffect if today
      if (!isTodayInZone(timezone, newDate)) {
        setSelectedTime(DEFAULT_TIME); // Default to 9:00 AM for future dates
      }
    }
  };

  const noAvailableTimes = isCurrentDay && timeOptions.length === 0;

  return (
    <RadixDialog
      isOpen={open}
      onClose={handleClose}
      title={
        isEdit ? t('scheduling.editModalTitle') : t('scheduling.modalTitle')
      }
      zIndex={1400}
    >
      <Flex direction="column" gap="3" className={styles.content}>
        <Text as="p" size="2" color="gray">
          {t('scheduling.modalBody')}
        </Text>

        <div className={styles.calendarWrapper}>
          <DatePicker
            value={selectedDate}
            onChange={handleDateChange}
            minDate="today"
            mode="single"
            size={1}
          />
        </div>

        <div>
          <Text as="label" size="2" weight="medium" mb="2">
            {t('scheduling.selectTime')}
          </Text>
          {noAvailableTimes ? (
            <Text size="2" color="red">
              {t('scheduling.noAvailableHours')}
            </Text>
          ) : (
            <Select.Root
              size="3"
              value={selectedTime?.toString()}
              onValueChange={value => setSelectedTime(parseInt(value))}
            >
              <Select.Trigger className={styles.timeSelect} />
              <Select.Content
                position="popper"
                className={styles.timeSelectContent}
              >
                {timeOptions.map(option => (
                  <Select.Item
                    key={option.value}
                    value={option.value.toString()}
                  >
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          )}
        </div>

        {error && (
          <Text size="2" className={styles.errorMessage}>
            {error}
          </Text>
        )}

        <Flex gap="3" justify="end">
          <Button variant="outline" size="3" onClick={handleClose}>
            {t('scheduling.cancel')}
          </Button>
          <Button
            variant="solid"
            size="3"
            onClick={handleSchedule}
            disabled={noAvailableTimes}
          >
            {isEdit ? t('scheduling.updateSchedule') : t('scheduling.schedule')}
          </Button>
        </Flex>
      </Flex>
    </RadixDialog>
  );
};
